import { useEffect, useState } from "react";
import axios from "axios";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TAGLINE = "Paperwork pageantry, by promise and plume.";
const LOGO_URL = "/cachet-logo.png";
const HEADSHOT_URL = "/annette-washington-portrait.webp";
const OFFICE_URL = "/capn-office-background.png";
const ADMIN_TOKEN_KEY = "cachet-admin-token";
const ADMIN_EXPIRES_KEY = "cachet-admin-expires-at";

const services = [
  {
    title: "Mobile Notary",
    description:
      "Discreet, punctual notarial service brought to homes, offices, hospitals, and agreed-upon meeting places across the Philadelphia metro.",
  },
  {
    title: "Remote Online Notarization (RON)",
    description:
      "Secure online notarization for eligible documents, offering polished convenience when an in-person appointment is not required.",
  },
  {
    title: "Loan Signing & Mortgage Closing",
    description:
      "Detail-minded signing support for purchase, refinance, seller, and related real estate closing documents.",
  },
];

const formatSubmissionDate = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
};

function AdminLogin() {
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [loginStatus, setLoginStatus] = useState({ type: "idle", message: "" });

  useEffect(() => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    const expiresAt = localStorage.getItem(ADMIN_EXPIRES_KEY);
    if (token && expiresAt && new Date(expiresAt) > new Date()) {
      window.history.replaceState({}, "", "/admin");
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setCredentials((current) => ({ ...current, [name]: value }));
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginStatus({ type: "loading", message: "Verifying credentials…" });
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      localStorage.setItem(ADMIN_TOKEN_KEY, response.data.token);
      localStorage.setItem(ADMIN_EXPIRES_KEY, response.data.expires_at);
      setLoginStatus({ type: "success", message: "Welcome back." });
      window.history.pushState({}, "", "/admin");
      window.dispatchEvent(new PopStateEvent("popstate"));
    } catch (error) {
      setLoginStatus({
        type: "error",
        message: error.response?.data?.detail || "Unable to log in. Please check the credentials.",
      });
    }
  };

  return (
    <div
      className="admin-shell"
      style={{ backgroundImage: `linear-gradient(135deg, rgba(26, 26, 26, 0.9), rgba(26, 77, 92, 0.88)), url(${OFFICE_URL})` }}
      data-testid="admin-login-page"
    >
      <div className="admin-card login-card" data-testid="admin-login-card">
        <a className="admin-logo-link" href="/" data-testid="admin-login-home-link">
          <img src={LOGO_URL} alt="Cachet & Plume Noir logo" data-testid="admin-login-logo" />
        </a>
        <p className="eyebrow" data-testid="admin-login-eyebrow">Private Office</p>
        <h1 data-testid="admin-login-heading">Admin Login</h1>
        <p className="admin-muted" data-testid="admin-login-copy">Secure access to Cachet & Plume Noir contact submissions.</p>
        <form className="admin-form" onSubmit={handleLogin} data-testid="admin-login-form">
          <label data-testid="admin-email-label">
            Email
            <input type="email" name="email" value={credentials.email} onChange={handleChange} required placeholder="Admin email" data-testid="admin-email-input" />
          </label>
          <label data-testid="admin-password-label">
            Password
            <input type="password" name="password" value={credentials.password} onChange={handleChange} required data-testid="admin-password-input" />
          </label>
          <button className="primary-button form-button" type="submit" disabled={loginStatus.type === "loading"} data-testid="admin-login-submit-button">
            {loginStatus.type === "loading" ? "Signing in…" : "Sign In"}
          </button>
          {loginStatus.message && <p className={`form-status ${loginStatus.type}`} data-testid="admin-login-status">{loginStatus.message}</p>}
        </form>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const [submissions, setSubmissions] = useState([]);
  const [dashboardStatus, setDashboardStatus] = useState({ type: "loading", message: "Loading submissions…" });
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [passwordStatus, setPasswordStatus] = useState({ type: "idle", message: "" });

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_EXPIRES_KEY);
    window.history.pushState({}, "", "/admin/login");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  useEffect(() => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    const expiresAt = localStorage.getItem(ADMIN_EXPIRES_KEY);
    if (!token || !expiresAt || new Date(expiresAt) <= new Date()) {
      logout();
      return;
    }

    const loadSubmissions = async () => {
      try {
        const response = await axios.get(`${API}/admin/contact-submissions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubmissions(response.data);
        setDashboardStatus({ type: "success", message: `${response.data.length} submission${response.data.length === 1 ? "" : "s"} found.` });
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          logout();
          return;
        }
        setDashboardStatus({ type: "error", message: "Unable to load submissions right now." });
      }
    };

    loadSubmissions();
  }, []);

  const handlePasswordInput = (event) => {
    const { name, value } = event.target;
    setPasswordForm((current) => ({ ...current, [name]: value }));
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordStatus({ type: "error", message: "New password and confirmation do not match." });
      return;
    }

    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    setPasswordStatus({ type: "loading", message: "Updating password…" });
    try {
      const response = await axios.post(
        `${API}/admin/change-password`,
        {
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPasswordStatus({ type: "success", message: response.data.message });
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      setTimeout(logout, 1200);
    } catch (error) {
      setPasswordStatus({
        type: "error",
        message: error.response?.data?.detail || "Unable to update password.",
      });
    }
  };

  return (
    <div
      className="admin-shell dashboard-shell"
      style={{ backgroundImage: `linear-gradient(135deg, rgba(26, 26, 26, 0.9), rgba(26, 77, 92, 0.88)), url(${OFFICE_URL})` }}
      data-testid="admin-dashboard-page"
    >
      <div className="admin-dashboard" data-testid="admin-dashboard-panel">
        <header className="admin-dashboard-header" data-testid="admin-dashboard-header">
          <div>
            <p className="eyebrow" data-testid="admin-dashboard-eyebrow">Private Office</p>
            <h1 data-testid="admin-dashboard-heading">Contact Submissions</h1>
            <p className="admin-muted" data-testid="admin-dashboard-status">{dashboardStatus.message}</p>
          </div>
          <button className="nav-cta admin-logout" onClick={logout} data-testid="admin-logout-button">Logout</button>
        </header>

        <div className="submissions-list" data-testid="admin-submissions-list">
          {submissions.length === 0 && dashboardStatus.type !== "loading" ? (
            <div className="empty-state" data-testid="admin-empty-state">No contact submissions yet.</div>
          ) : (
            submissions.map((submission) => (
              <article className="submission-card" key={submission.id} data-testid={`submission-card-${submission.id}`}>
                <div className="submission-meta">
                  <div>
                    <h2 data-testid={`submission-name-${submission.id}`}>{submission.name}</h2>
                    <a href={`mailto:${submission.email}`} data-testid={`submission-email-${submission.id}`}>{submission.email}</a>
                  </div>
                  <time data-testid={`submission-timestamp-${submission.id}`}>{formatSubmissionDate(submission.created_at)}</time>
                </div>
                <p data-testid={`submission-message-${submission.id}`}>{submission.message}</p>
              </article>
            ))
          )}
        </div>

        <section className="password-panel" data-testid="admin-password-panel">
          <div>
            <p className="eyebrow" data-testid="admin-password-eyebrow">Security</p>
            <h2 data-testid="admin-password-heading">Change Password</h2>
            <p className="admin-muted" data-testid="admin-password-copy">Update your admin password. You will be signed out after a successful change.</p>
          </div>
          <form className="admin-form password-form" onSubmit={handlePasswordChange} data-testid="admin-password-form">
            <label data-testid="current-password-label">
              Current password
              <input type="password" name="current_password" value={passwordForm.current_password} onChange={handlePasswordInput} required data-testid="current-password-input" />
            </label>
            <label data-testid="new-password-label">
              New password
              <input type="password" name="new_password" value={passwordForm.new_password} onChange={handlePasswordInput} required minLength="12" data-testid="new-password-input" />
            </label>
            <label data-testid="confirm-password-label">
              Confirm new password
              <input type="password" name="confirm_password" value={passwordForm.confirm_password} onChange={handlePasswordInput} required minLength="12" data-testid="confirm-password-input" />
            </label>
            <button className="primary-button form-button" type="submit" disabled={passwordStatus.type === "loading"} data-testid="change-password-submit-button">
              {passwordStatus.type === "loading" ? "Updating…" : "Update Password"}
            </button>
            {passwordStatus.message && <p className={`form-status ${passwordStatus.type}`} data-testid="change-password-status">{passwordStatus.message}</p>}
          </form>
        </section>
      </div>
    </div>
  );
}

function App() {
  const [path, setPath] = useState(window.location.pathname);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState({ type: "idle", message: "" });

  useEffect(() => {
    const handleRouteChange = () => setPath(window.location.pathname);
    window.addEventListener("popstate", handleRouteChange);
    return () => window.removeEventListener("popstate", handleRouteChange);
  }, []);

  if (path === "/admin/login") {
    return <AdminLogin />;
  }

  if (path === "/admin") {
    return <AdminDashboard />;
  }

  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: "loading", message: "Sending your note…" });

    try {
      const response = await axios.post(`${API}/contact`, form);
      setStatus({ type: "success", message: response.data.message });
      setForm({ name: "", email: "", message: "" });
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.response?.data?.detail ||
          "Something interrupted the message. Please try again or email us.",
      });
    }
  };

  return (
    <div className="site-shell" data-testid="landing-page-shell">
      <header className="hero-section" id="top" data-testid="hero-section">
        <img className="hero-office-image" src={OFFICE_URL} alt="Elegant Cachet & Plume Noir office" data-testid="hero-office-image" />
        <div className="hero-veil" data-testid="hero-image-opacity-overlay" />
        <nav className="topbar" data-testid="top-navigation">
          <a className="brand-mark" href="#top" data-testid="top-brand-link">
            <img src={LOGO_URL} alt="Cachet & Plume Noir logo" data-testid="top-logo-image" />
          </a>
          <button className="nav-cta" onClick={scrollToContact} data-testid="top-book-button">
            Book a Notary
          </button>
        </nav>

        <div className="hero-grid">
          <div className="hero-copy">
            <p className="eyebrow" data-testid="hero-eyebrow">
              Philadelphia Metro Mobile & Remote Notary
            </p>
            <h1 data-testid="hero-business-name">Cachet & Plume Noir</h1>
            <p className="tagline" data-testid="hero-tagline">
              {TAGLINE}
            </p>
            <button className="primary-button" onClick={scrollToContact} data-testid="hero-book-button">
              Book a Notary
            </button>
          </div>
        </div>
      </header>

      <main>
        <section className="section services-section" data-testid="services-section">
          <div className="section-heading">
            <p className="eyebrow" data-testid="services-eyebrow">Services</p>
            <h2 data-testid="services-heading">Notarial service with ceremony and care.</h2>
          </div>
          <div className="service-grid" data-testid="services-card-grid">
            {services.map((service, index) => (
              <article className="service-card" key={service.title} data-testid={`service-card-${index + 1}`}>
                <span className="service-number" data-testid={`service-card-${index + 1}-number`}>
                  0{index + 1}
                </span>
                <h3 data-testid={`service-card-${index + 1}-title`}>{service.title}</h3>
                <p data-testid={`service-card-${index + 1}-description`}>{service.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section name-note-section" data-testid="name-note-section">
          <div className="ornament" data-testid="name-note-ornament">✦</div>
          <div>
            <h2 data-testid="name-note-heading">A Note on the Name</h2>
            <div className="name-note-copy" data-testid="name-note-copy">
              <p data-testid="name-note-paragraph-1">Cachet & Plume Noir is more than a name. It is the heritage of the work itself.</p>
              <p data-testid="name-note-paragraph-2">In philately and old French tradition, a cachet is an officially stamped mark, a seal of authenticity pressed into wax to certify the authority behind a document. Lettres de cachet were letters signed by the King of France, countersigned by his ministers, and closed with the royal seal. The cachet was not decoration. It was the difference between a piece of paper and an instrument of law.</p>
              <p data-testid="name-note-paragraph-3">A plume, the feather pen, was the instrument that gave the document its voice. The signature. The intent. The hand of the signer made permanent.</p>
              <p data-testid="name-note-paragraph-4">{"And noir. Black. The color of ink that doesn't fade. The color of focus. The color of every signature that ever mattered."}</p>
              <p data-testid="name-note-paragraph-5">{"Today, the wax has been replaced by a notary's stamp, the quill by a fountain pen, the royal court by a kitchen table or a Zoom screen. But the essence is unchanged: a witness, a seal, and the weight of a signature done right."}</p>
              <p data-testid="name-note-paragraph-6">The seal. The pen. The standard.</p>
            </div>
          </div>
        </section>

        <section className="section meet-section" data-testid="meet-annette-section">
          <div className="portrait-frame" data-testid="annette-portrait-frame">
            <img src={HEADSHOT_URL} alt="Annette Washington, Founder of Cachet & Plume Noir Mobile Notary Services." data-testid="annette-headshot-image" />
          </div>
          <div className="meet-copy">
            <p className="eyebrow" data-testid="meet-annette-eyebrow">Meet Annette</p>
            <h2 data-testid="meet-annette-heading">S. Annette Washington</h2>
            <p className="title-line" data-testid="meet-annette-title">Pennsylvania Notary Public</p>
            <p data-testid="meet-annette-bio">
              Annette brings a poised, client-centered approach to mobile and remote notarization. Her practice is built on preparedness, respect for privacy, and a calm signing experience from first message to final seal.
            </p>
          </div>
        </section>

        <section className="section service-area-section" data-testid="service-area-section">
          <p className="eyebrow" data-testid="service-area-eyebrow">Service Area</p>
          <h2 data-testid="service-area-heading">Serving Delaware County, Philadelphia, and the Main Line.</h2>
          <p data-testid="service-area-copy">Remote online notarization available statewide.</p>
        </section>

        <section className="section contact-section" id="contact" data-testid="contact-section">
          <div className="contact-intro">
            <p className="eyebrow" data-testid="contact-eyebrow">Contact</p>
            <h2 data-testid="contact-heading">Reserve a refined notary appointment.</h2>
            <p data-testid="contact-copy">
              Share your details and the document type or timing you have in mind. Cachet & Plume Noir will respond with next steps.
            </p>
            <div className="contact-details" data-testid="contact-details">
              <a href="tel:+16106799152" data-testid="contact-phone-link">(610) 679-9152</a>
              <a href="mailto:hello@cachetandplumenoir.com" data-testid="contact-email-link">
                Email us
              </a>
            </div>
          </div>

          <form className="contact-form" onSubmit={handleSubmit} data-testid="contact-form">
            <label data-testid="contact-name-label">
              Name
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                minLength="2"
                placeholder="Your full name"
                data-testid="contact-name-input"
              />
            </label>
            <label data-testid="contact-email-label">
              Email
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                placeholder="you@example.com"
                data-testid="contact-email-input"
              />
            </label>
            <label data-testid="contact-message-label">
              Message
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                required
                minLength="5"
                rows="6"
                placeholder="Tell us what you need notarized and your preferred timing."
                data-testid="contact-message-input"
              />
            </label>
            <button className="primary-button form-button" type="submit" disabled={status.type === "loading"} data-testid="contact-submit-button">
              {status.type === "loading" ? "Sending…" : "Send Request"}
            </button>
            {status.message && (
              <p className={`form-status ${status.type}`} data-testid="contact-form-status">
                {status.message}
              </p>
            )}
          </form>
        </section>
      </main>

      <footer className="site-footer" data-testid="site-footer">
        <p data-testid="footer-business-name">Cachet & Plume Noir LLC</p>
        <p data-testid="footer-tagline">{TAGLINE}</p>
        <div className="footer-links" data-testid="footer-contact-links">
          <a href="tel:+16106799152" data-testid="footer-phone-link">(610) 679-9152</a>
          <a href="mailto:hello@cachetandplumenoir.com" data-testid="footer-email-link">Email us</a>
        </div>
        <small data-testid="footer-copyright">© 2026 Cachet & Plume Noir LLC.</small>
      </footer>
    </div>
  );
}

export default App;
